import {
  Briefcase,
  Mic,
  Film,
  PenTool,
  Sparkles,
  Hammer,
  Users,
  GraduationCap,
  HeartHandshake,
  Compass,
  Rocket,
  ShoppingBag,
  Globe,
  Megaphone,
  Truck,
  Video,
} from "lucide-react";
import { PRICING } from "@/lib/pricing";

const pctOff = (t) =>
  t.strike ? `${Math.round(((t.strike - t.price) / t.strike) * 100)}% OFF` : "Limited Time Offer";

// Pricing lives in @/lib/pricing (live, admin-editable).

export const COURSES_LIST = [
  {
    slug: "video-editing",
    icon: Film,
    title: "Ultimate Video Editing Course",
    short: "Storytelling, motion and post-production for modern creators.",
    cover: "https://images.unsplash.com/photo-1574717024653-61fd2cf4d44d?auto=format&fit=crop&w=1200&q=80",
    get price() { return PRICING.coursePrice; },
    duration: "10 weeks",
    level: "Beginner → Pro",
  },
  {
    slug: "freelancing",
    icon: Briefcase,
    title: "Freelancing Mastery Course",
    short: "Land clients, price your work, build a sustainable solo career.",
    cover: "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=1200&q=80",
    get price() { return PRICING.coursePrice; },
    duration: "8 weeks",
    level: "Beginner → Pro",
  },
  {
    slug: "content-creation",
    icon: Mic,
    title: "Content Creation Mastery Course",
    short: "Build an audience across reels, shorts and long-form formats.",
    cover: "https://images.unsplash.com/photo-1499951360447-b19be8fe80f5?auto=format&fit=crop&w=1200&q=80",
    get price() { return PRICING.coursePrice; },
    duration: "10 weeks",
    level: "All Levels",
  },
  {
    slug: "graphic-designing",
    icon: PenTool,
    title: "Graphic Designing Mastery Course",
    short: "Visual systems, branding and product UI with Figma & Adobe.",
    cover: "https://images.unsplash.com/photo-1626785774573-4b799315345d?auto=format&fit=crop&w=1200&q=80",
    get price() { return PRICING.coursePrice; },
    duration: "10 weeks",
    level: "Beginner → Pro",
  },
  {
    slug: "artificial-intelligence",
    icon: Sparkles,
    title: "Artificial Intelligence Mastery Course",
    short: "Practical AI: prompts, agents, automations and product use-cases.",
    cover: "https://images.unsplash.com/photo-1677442136019-21780ecad995?auto=format&fit=crop&w=1200&q=80",
    get price() { return PRICING.coursePrice; },
    duration: "10 weeks",
    level: "All Levels",
  },
  {
    slug: "digital-product-selling",
    icon: ShoppingBag,
    title: "Digital Product Selling Course",
    short: "Build, package and sell digital products that earn while you sleep.",
    cover: "https://images.unsplash.com/photo-1742837581522-111d23b69a04?auto=format&fit=crop&w=1200&q=80",
    get price() { return PRICING.coursePrice; },
    duration: "8 weeks",
    level: "Beginner → Pro",
  },
  {
    slug: "ai-website-designing",
    icon: Globe,
    title: "AI Website Designing Course",
    short: "Design and ship professional websites with AI — no heavy coding.",
    cover: "https://images.unsplash.com/photo-1772272935464-2e90d8218987?auto=format&fit=crop&w=1200&q=80",
    get price() { return PRICING.coursePrice; },
    duration: "8 weeks",
    level: "Beginner → Pro",
  },
  {
    slug: "digital-marketing",
    icon: Megaphone,
    title: "Digital Marketing Course",
    short: "Audience, content, leads and paid ads — the complete growth stack.",
    cover: "https://images.unsplash.com/photo-1526628953301-3e589a6a8b74?auto=format&fit=crop&w=1200&q=80",
    get price() { return PRICING.coursePrice; },
    duration: "10 weeks",
    level: "All Levels",
  },
  {
    slug: "dropshipping",
    icon: Truck,
    title: "Dropshipping Course",
    short: "Product research, stores, suppliers and scaling — end to end.",
    cover: "https://images.unsplash.com/photo-1786026632846-6d411011130f?auto=format&fit=crop&w=1200&q=80",
    get price() { return PRICING.coursePrice; },
    duration: "8 weeks",
    level: "Beginner → Pro",
  },
  {
    slug: "ugc-content-creation",
    icon: Video,
    title: "UGC Content Creation Course",
    short: "Create brand-ready UGC, build a portfolio and land paid deals.",
    cover: "https://images.unsplash.com/photo-1617349035560-b32339aeeb14?auto=format&fit=crop&w=1200&q=80",
    get price() { return PRICING.coursePrice; },
    duration: "8 weeks",
    level: "All Levels",
  },
];

// Premium "buy everything separately" breakdown — derived, never hardcoded.
export const premiumValueList = () => [
  ...COURSES_LIST.map((c) => ({ label: c.title, value: PRICING.coursePrice })),
  ...PRICING.premiumPerks,
];

// Long-form content per course (placeholder, editable later)
export const COURSE_DETAILS = {
  freelancing: {
    title: "Freelancing Mastery Course",
    tagline: "Turn your skills into sustainable, location-free income.",
    overview:
      "A practical playbook for becoming a profitable freelancer — positioning, pricing, finding clients, scoping projects, and delivering results that get you referred. Designed for beginners and experienced solo operators alike.",
    forWho: [
      "Students looking to start earning on their own terms",
      "Professionals wanting a high-leverage side income",
      "Creators or designers ready to charge premium rates",
    ],
    youLearn: [
      "Choosing a niche and crafting your offer",
      "Pricing strategy: hourly, project, value-based",
      "Cold outreach, inbound positioning and proposals that win",
      "Contracts, scope management and client onboarding",
      "Delivery systems and referrals that compound",
    ],
    outcomes: [
      "A clear freelance offer with positioning that stands out",
      "A portfolio and case studies tailored to your niche",
      "A repeatable client-acquisition workflow",
    ],
    tools: ["Upwork", "Fiverr", "LinkedIn", "Notion", "Loom", "Stripe", "Razorpay"],
    careers: [
      "Independent Freelancer (₹40K–₹2L/mo)",
      "Service Agency Owner",
      "Consultant / Solopreneur",
      "Productized Service Builder",
    ],
    duration: "8 weeks · self-paced",
    level: "Beginner → Pro",
    totalSessions: "45+ Sessions",
    curriculum: [
      {
        title: "Freelancing Foundations",
        sessions: [
          "Welcome & Income Reality Check",
          "Freelance vs Job vs Business",
          "Choosing the Right Niche",
          "Pricing Psychology Basics",
          "Setting Up Your Workspace",
        ],
      },
      {
        title: "Positioning & Personal Brand",
        sessions: [
          "Crafting a Compelling Offer",
          "LinkedIn Profile Optimization",
          "Portfolio Site Essentials",
          "Case Study Writing",
          "Social Proof Strategy",
        ],
      },
      {
        title: "Finding Clients (Inbound + Outbound)",
        sessions: [
          "Fiverr Gig Setup & SEO",
          "Upwork Profile & Proposals",
          "LinkedIn Outreach Frameworks",
          "Cold Email That Converts",
          "Referral & Word-of-Mouth Systems",
        ],
      },
      {
        title: "Pricing & Proposals",
        sessions: [
          "Hourly vs Project vs Value-Based",
          "Discovery Calls & Scoping",
          "Writing Winning Proposals",
          "Negotiation & Objection Handling",
        ],
      },
      {
        title: "Client Delivery & Operations",
        sessions: [
          "Contracts & Scope Management",
          "Onboarding & Kickoff Calls",
          "Project Management Workflows",
          "Communication SOPs",
          "Handling Difficult Clients",
        ],
      },
      {
        title: "Payments, Tax & Tools",
        sessions: [
          "Invoicing & Payment Gateways",
          "GST, Income Tax Basics (India)",
          "International Payments",
          "Stack: Notion, Loom, Slack, Calendly",
        ],
      },
      {
        title: "Scaling Your Freelance Business",
        sessions: [
          "Productizing Your Service",
          "Hiring Your First Sub-Contractor",
          "Building Recurring Revenue",
          "From Freelancer to Agency",
        ],
      },
      {
        title: "Bonus Module",
        bonus: true,
        sessions: [
          "Proposal Templates Pack",
          "Email Outreach Scripts",
          "Contract Templates",
          "Pricing Calculator",
          "Client Onboarding Checklist",
        ],
      },
    ],
  },
  "content-creation": {
    title: "Content Creation Mastery Course",
    tagline: "Build an audience that compounds — across every platform.",
    overview:
      "Learn the modern playbook for building a personal brand or media business through short-form, long-form and written content. From hook crafting to thumbnail design to consistent publishing systems — everything you need to grow.",
    forWho: [
      "Aspiring creators starting from zero",
      "Founders/professionals building thought leadership",
      "Freelancers using content to attract clients",
    ],
    youLearn: [
      "Niche selection and content pillars",
      "Hook writing and storytelling frameworks",
      "Short-form for Instagram, YouTube Shorts, TikTok",
      "Long-form YouTube and written content systems",
      "Posting cadence, analytics and growth loops",
    ],
    outcomes: [
      "A clear content strategy aligned to your goals",
      "A library of templates and frameworks for daily posting",
      "Initial audience traction with measurable growth",
    ],
    tools: ["Instagram", "YouTube", "X (Twitter)", "LinkedIn", "Notion", "Canva", "CapCut", "ChatGPT"],
    careers: [
      "Personal Brand Creator",
      "Niche Content Creator",
      "Brand Content Strategist",
      "YouTuber / Podcaster",
      "Newsletter Operator",
    ],
    duration: "10 weeks · self-paced + live",
    level: "All levels",
    totalSessions: "50+ Sessions",
    curriculum: [
      {
        title: "Creator Foundations",
        sessions: [
          "Why Content in 2026",
          "Choosing Your Niche",
          "Audience Avatar Design",
          "Platform Selection Strategy",
          "Setting Up Your Brand",
        ],
      },
      {
        title: "Hook & Storytelling Mastery",
        sessions: [
          "The Anatomy of a Viral Hook",
          "5 Hook Templates that Always Work",
          "Storytelling for Social Media",
          "Emotional Triggers",
          "Editing for Retention",
        ],
      },
      {
        title: "Short-Form Content (Reels & Shorts)",
        sessions: [
          "Reels Strategy & Trends",
          "Filming with Just a Phone",
          "Editing with CapCut",
          "Captions & Subtitles",
          "Beat Sync & Pacing",
          "Posting Cadence",
        ],
      },
      {
        title: "Long-Form Content (YouTube)",
        sessions: [
          "Channel Setup & Niche",
          "Title & Thumbnail Frameworks",
          "Scripting Long-Form Videos",
          "Editing for Retention",
          "Analytics & CTR Optimization",
        ],
      },
      {
        title: "Written Content (X, LinkedIn, Newsletter)",
        sessions: [
          "Writing Tweets that Spread",
          "LinkedIn Posts that Get Reach",
          "Building a Newsletter",
          "Cross-Posting Strategy",
        ],
      },
      {
        title: "Audience Growth & Monetization",
        sessions: [
          "First 1,000 Followers Playbook",
          "Algorithm Hacks (Per Platform)",
          "Collaboration & Cross-Promotion",
          "Brand Deals & UGC Pricing",
          "Course / Product Launch Basics",
        ],
      },
      {
        title: "Systems & Consistency",
        sessions: [
          "Content Calendar in Notion",
          "Batching & Recording Days",
          "Repurposing 1 Idea into 5 Posts",
          "Analytics Dashboard",
        ],
      },
      {
        title: "Bonus Module",
        bonus: true,
        sessions: [
          "100 Viral Hook Templates",
          "Thumbnail Pack",
          "Notion Content Calendar Template",
          "Brand Deal Email Templates",
          "Audience Avatar Worksheet",
        ],
      },
    ],
  },
  "video-editing": {
    title: "Ultimate Video Editing Course",
    tagline: "Tell stories that hold attention — frame by frame.",
    overview:
      "From foundational cuts to advanced motion and color, this course teaches you to edit like a working professional. Built around real-world projects so your portfolio compounds with every module.",
    forWho: [
      "Aspiring editors entering the creator economy",
      "Content creators wanting to self-edit at a pro level",
      "Freelancers who want to add a high-paying skill",
    ],
    youLearn: [
      "Premiere Pro / DaVinci Resolve / CapCut workflows",
      "Story structure, pacing and tension in edits",
      "Sound design, music and SFX",
      "Color grading and finishing",
      "Motion graphics, captions and platform-native formats",
    ],
    outcomes: [
      "A portfolio of 3-5 polished edits across formats",
      "Confidence to take freelance editing gigs",
      "Strong fundamentals for an editor career",
    ],
    tools: ["Premiere Pro", "After Effects", "DaVinci Resolve", "CapCut", "Final Cut Pro", "Audition"],
    careers: [
      "Freelance Video Editor (₹30K–₹1.5L/mo)",
      "YouTube / Reels Editor for Creators",
      "Brand Content Editor",
      "Motion Graphics Designer",
      "Post-Production Lead",
    ],
    duration: "10 weeks · project-based",
    level: "Beginner → Advanced",
    totalSessions: "60+ Sessions",
    curriculum: [
      {
        title: "Video Editing Foundations",
        sessions: [
          "Welcome & Course Overview",
          "Understanding Video Editing Industry",
          "Editing Mindset & Career Opportunities",
          "Mobile vs PC Editing",
          "Software & Tools Setup",
        ],
      },
      {
        title: "Mobile Editing Mastery",
        sessions: [
          "CapCut Complete Setup",
          "Understanding Timeline",
          "Cutting & Trimming Videos",
          "Speed Control Techniques",
          "Audio Synchronization",
          "Text & Caption Editing",
          "Basic Transitions",
        ],
      },
      {
        title: "Reels & Shorts Editing",
        sessions: [
          "Viral Reels Framework",
          "Hook Creation Strategy",
          "Beat Sync Editing",
          "Trending Reels Structure",
          "Subtitle Editing",
          "Retention Editing",
        ],
      },
      {
        title: "Advanced Mobile Editing",
        sessions: [
          "Keyframe Animation",
          "Motion Blur Effects",
          "Green Screen Editing",
          "Cinematic Effects",
          "Advanced Transitions",
          "Professional Reel Creation",
        ],
      },
      {
        title: "Content Creator Editing",
        sessions: [
          "Personal Brand Videos",
          "Educational Content Editing",
          "Motivational Reels",
          "Storytelling Framework",
          "Documentary Style Editing",
        ],
      },
      {
        title: "Premiere Pro Mastery",
        sessions: [
          "Premiere Pro Interface",
          "Timeline Management",
          "Professional Workflow",
          "Multi-track Editing",
          "Audio Editing",
          "Professional Text Animation",
        ],
      },
      {
        title: "Color Grading & Cinematic Looks",
        sessions: [
          "Color Correction Basics",
          "Cinematic Color Grading",
          "LUTs & Presets",
          "Film Look Creation",
        ],
      },
      {
        title: "After Effects For Editors",
        sessions: [
          "Introduction To After Effects",
          "Motion Graphics",
          "Logo Animation",
          "Intro & Outro Creation",
        ],
      },
      {
        title: "YouTube Editing Mastery",
        sessions: [
          "Talking Head Videos",
          "Long Form Editing",
          "Retention Editing Techniques",
          "YouTube Storytelling",
        ],
      },
      {
        title: "Freelancing & Client Acquisition",
        sessions: [
          "Fiverr Setup",
          "Upwork Setup",
          "Finding Clients",
          "Proposal Writing",
          "Pricing Strategy",
          "Client Communication",
        ],
      },
      {
        title: "Portfolio Building",
        sessions: [
          "Portfolio Creation",
          "Showreel Creation",
          "Behance Setup",
          "Instagram Portfolio",
        ],
      },
      {
        title: "Bonus Module",
        bonus: true,
        sessions: [
          "Transition Presets Pack",
          "Color Grading Presets",
          "100 Viral Reel Ideas",
          "Client Proposal Templates",
          "Invoice Templates",
          "Personal Branding Blueprint",
        ],
      },
    ],
  },
  "graphic-designing": {
    title: "Graphic Designing Mastery Course",
    tagline: "Design that earns attention — and revenue.",
    overview:
      "A foundational + applied design course covering visual systems, type, color, layout and brand. Move from copying templates to confidently designing across brand, social and product surfaces.",
    forWho: [
      "Beginners learning design as a high-income skill",
      "Founders building a brand identity",
      "Marketers and content creators needing visual fluency",
    ],
    youLearn: [
      "Design principles: hierarchy, contrast, balance, type",
      "Figma fundamentals and component thinking",
      "Brand identity and visual systems",
      "Social media and ad creatives that perform",
      "Building a portfolio and pricing client work",
    ],
    outcomes: [
      "A complete brand or identity project for your portfolio",
      "Daily design fluency with strong fundamentals",
      "Ability to take on freelance design work",
    ],
    tools: ["Figma", "Photoshop", "Illustrator", "InDesign", "Canva", "Adobe Express"],
    careers: [
      "Freelance Graphic Designer",
      "Brand Identity Designer",
      "UI Designer (entry-level)",
      "Social Media Designer",
      "Print & Packaging Designer",
    ],
    duration: "10 weeks · project-based",
    level: "Beginner → Pro",
    totalSessions: "55+ Sessions",
    curriculum: [
      {
        title: "Design Foundations",
        sessions: [
          "What Makes Great Design",
          "Hierarchy, Contrast & Balance",
          "Color Theory in Practice",
          "Typography Essentials",
          "Grids & Layout",
        ],
      },
      {
        title: "Figma Mastery",
        sessions: [
          "Figma Interface & Shortcuts",
          "Auto Layout",
          "Components & Variants",
          "Design Systems Basics",
          "Prototyping",
        ],
      },
      {
        title: "Photoshop & Illustrator Essentials",
        sessions: [
          "Photoshop for Designers",
          "Photo Manipulation",
          "Illustrator Pen Tool Mastery",
          "Vector Logos",
          "Mockups & Presentation",
        ],
      },
      {
        title: "Brand Identity Design",
        sessions: [
          "Brand Discovery & Brief",
          "Logo Design Process",
          "Color Palettes & Typography",
          "Brand Guidelines",
          "Logo Animation Basics",
        ],
      },
      {
        title: "Social Media & Ad Creatives",
        sessions: [
          "Instagram Carousel Design",
          "Story & Reel Templates",
          "Ad Creative Frameworks",
          "Thumbnail Design",
          "A/B Testing Visuals",
        ],
      },
      {
        title: "UI Design Basics",
        sessions: [
          "Mobile App UI Basics",
          "Web Landing Pages",
          "Buttons, Forms & States",
          "Designing for Dark Mode",
        ],
      },
      {
        title: "Print & Packaging",
        sessions: [
          "Print Specs (CMYK, Bleed)",
          "Business Cards & Stationery",
          "Posters & Flyers",
          "Packaging Mockups",
        ],
      },
      {
        title: "Freelance Design Career",
        sessions: [
          "Building a Design Portfolio",
          "Behance & Dribbble Setup",
          "Pricing Design Work",
          "Client Briefs & Revisions",
          "Niche Down for Higher Rates",
        ],
      },
      {
        title: "Bonus Module",
        bonus: true,
        sessions: [
          "100 Color Palette Pack",
          "Logo Templates",
          "Social Media Template Pack",
          "Client Brief Templates",
          "Design Resource Library",
        ],
      },
    ],
  },
  "artificial-intelligence": {
    title: "Artificial Intelligence Mastery Course",
    tagline: "Use AI as your unfair advantage — at work and in your business.",
    overview:
      "A practical, builder-first course on using AI in the real world: prompts, workflows, agents, automations and product use-cases. Strong on application, light on hype.",
    forWho: [
      "Professionals wanting to be 10x more productive with AI",
      "Freelancers offering AI services to clients",
      "Builders wanting to ship AI-powered products",
    ],
    youLearn: [
      "Prompt engineering frameworks that actually work",
      "Building agents and automations with no-code tools",
      "Common APIs (OpenAI, Anthropic, Gemini, image/video)",
      "AI workflows for content, marketing, sales and ops",
      "Shipping a portfolio AI project",
    ],
    outcomes: [
      "A working AI-powered project in your portfolio",
      "Repeatable prompts and automations for daily work",
      "Confidence to take AI-related freelance work",
    ],
    tools: ["ChatGPT", "Claude", "Gemini", "Midjourney", "Nano Banana", "Sora 2", "Zapier", "n8n", "Make"],
    careers: [
      "AI Prompt Engineer",
      "AI Automation Consultant",
      "AI Content Producer (Image / Video)",
      "AI Workflow Builder",
      "AI Product Builder / Solopreneur",
    ],
    duration: "10 weeks · project-based",
    level: "All levels",
    totalSessions: "48+ Sessions",
    curriculum: [
      {
        title: "AI Foundations",
        sessions: [
          "How LLMs Actually Work (No Math)",
          "ChatGPT vs Claude vs Gemini",
          "Tokens, Context & Memory",
          "Free vs Paid Tools (2026)",
          "Setting Up Your AI Stack",
        ],
      },
      {
        title: "Prompt Engineering",
        sessions: [
          "The 5 Prompt Frameworks",
          "Role + Task + Context + Format",
          "Chain-of-Thought Prompts",
          "Few-Shot Examples",
          "Prompt Templates Library",
        ],
      },
      {
        title: "AI for Content Creation",
        sessions: [
          "AI Writing for Social Media",
          "Long-Form Writing with AI",
          "AI Voice & Podcast Workflows",
          "Image Generation (Midjourney, Nano Banana)",
          "Video Generation (Sora 2 basics)",
        ],
      },
      {
        title: "AI for Productivity",
        sessions: [
          "Email Drafting Workflows",
          "Meeting Summary Automations",
          "Research with Perplexity & Notion AI",
          "Data Analysis with ChatGPT",
        ],
      },
      {
        title: "AI Automations (No-Code)",
        sessions: [
          "Zapier × OpenAI",
          "n8n / Make Workflows",
          "Building a Personal AI Assistant",
          "Connecting AI to Google Sheets",
          "Sending Automated AI Replies",
        ],
      },
      {
        title: "AI Agents & Builders",
        sessions: [
          "What Are AI Agents (Really)",
          "Building a Custom GPT",
          "Voice Agents with ElevenLabs",
          "Multi-Step Agent Workflows",
        ],
      },
      {
        title: "AI as a Freelance Service",
        sessions: [
          "Top 10 AI Services to Offer",
          "Pricing AI Work",
          "Building Case Studies",
          "Pitching AI to Local Businesses",
          "Long-Term Retainer Pricing",
        ],
      },
      {
        title: "Capstone Project",
        sessions: [
          "Choosing Your AI Project",
          "Designing the Workflow",
          "Building & Testing",
          "Launching & Sharing",
        ],
      },
      {
        title: "Bonus Module",
        bonus: true,
        sessions: [
          "200 Prompt Templates",
          "Automation Recipe Pack",
          "AI Services Pricing Guide",
          "Client Proposal Templates",
          "Tool Comparison Cheatsheet",
        ],
      },
    ],
  },
  "digital-product-selling": {
    title: "Digital Product Selling Course",
    tagline: "Build it once. Package it well. Sell it forever.",
    overview:
      "Identify a profitable digital product idea, create the product, package it professionally, set up a selling system, market it and generate sales online. A complete, action-based path from idea to first sales.",
    forWho: [
      "Beginners who want an online income asset without inventory",
      "Creators and freelancers ready to productize their knowledge",
      "Working professionals building a scalable side business",
    ],
    youLearn: [
      "Finding and validating a profitable digital product idea",
      "Creating ebooks, templates, checklists and digital resources (with AI)",
      "Product naming, covers, mockups and premium packaging",
      "Landing pages, payments and automated delivery",
      "Content marketing, offers and conversion-focused selling",
    ],
    outcomes: [
      "One complete digital product, packaged and ready to sell",
      "A working selling system with payments and automated delivery",
      "A repeatable marketing playbook for every future product",
    ],
    tools: ["Canva", "ChatGPT", "Notion", "Gumroad", "Razorpay", "Instagram", "Google Docs"],
    careers: [
      "Digital Product Creator",
      "Template / Resource Seller",
      "Course & Ebook Creator",
      "Digital Product Consultant",
      "Solopreneur / Creator-Entrepreneur",
    ],
    duration: "8 weeks · action-based",
    level: "Beginner → Pro",
    totalSessions: "44+ Sessions",
    curriculum: [
      {
        title: "Digital Product Fundamentals",
        sessions: [
          "What Is a Digital Product?",
          "Types of Digital Products",
          "Digital Products vs Physical Products",
          "Finding Profitable Product Opportunities",
          "Choosing the Right Niche",
          "Identifying Customer Problems",
        ],
      },
      {
        title: "Product Research & Validation",
        sessions: [
          "Finding Product Ideas",
          "Competitor Research",
          "Market Demand Research",
          "Identifying Customer Pain Points",
          "Validating Your Product Idea",
          "Creating a Simple Product Offer",
        ],
      },
      {
        title: "Creating Your Digital Product",
        sessions: [
          "Choosing the Right Product Format",
          "Creating Ebooks & Guides",
          "Creating Templates & Checklists",
          "Creating Digital Resources",
          "Using AI to Speed Up Product Creation",
          "Quality Checking Your Product",
        ],
      },
      {
        title: "Product Branding & Packaging",
        sessions: [
          "Product Naming",
          "Creating a Strong Product Cover",
          "Branding Basics",
          "Creating a Professional Product Experience",
          "Writing Product Descriptions",
          "Creating Product Mockups",
        ],
      },
      {
        title: "Selling System",
        sessions: [
          "Choosing Where to Sell",
          "Creating a Product Landing Page",
          "Setting Your Product Price",
          "Payment & Delivery Setup",
          "Creating an Automated Delivery System",
          "Customer Purchase Journey",
        ],
      },
      {
        title: "Marketing & Sales",
        sessions: [
          "Content Marketing for Digital Products",
          "Instagram Marketing",
          "Reels for Product Promotion",
          "Lead Generation",
          "Creating Offers & Bonuses",
          "Conversion-Focused Calls to Action",
        ],
      },
      {
        title: "Scaling",
        sessions: [
          "Tracking Product Sales",
          "Improving Conversion Rate",
          "Creating Product Bundles",
          "Upselling & Cross-Selling",
          "Building Multiple Digital Products",
          "Creating a Digital Product Business System",
        ],
      },
      {
        title: "Final Project",
        sessions: [
          "Create, package and launch one complete digital product with a basic selling system",
          "Submit your product page & delivery flow for mentor review",
        ],
      },
    ],
  },
  "ai-website-designing": {
    title: "AI Website Designing Course",
    tagline: "Ship professional websites with AI as your design partner.",
    overview:
      "Use modern AI tools to plan, design, build and improve professional websites without advanced coding knowledge — then turn that skill into paid client work.",
    forWho: [
      "Beginners who want to build websites without learning to code deeply",
      "Freelancers adding a high-demand service to their offer",
      "Founders and creators who need to ship their own site fast",
    ],
    youLearn: [
      "Website structure, UI/UX fundamentals and planning before building",
      "AI website workflows and prompt writing that produces usable designs",
      "Layout, typography, colour and high-converting section design",
      "Building landing, business and portfolio websites with AI",
      "Testing, basic SEO, domains, hosting and publishing",
    ],
    outcomes: [
      "A complete professional website built and published by you",
      "A reusable AI website workflow from brief to launch",
      "A client-ready service offer with packages and pricing",
    ],
    tools: ["ChatGPT", "Emergent", "Figma", "Framer", "Webflow", "Canva", "Google Fonts", "Vercel"],
    careers: [
      "AI Website Designer",
      "Freelance Web Designer",
      "Landing Page Specialist",
      "No-Code Website Builder",
      "Web Design Agency Owner",
    ],
    duration: "8 weeks · project-based",
    level: "Beginner → Pro",
    totalSessions: "44+ Sessions",
    curriculum: [
      {
        title: "Website Fundamentals",
        sessions: [
          "How Websites Work",
          "Website Types",
          "Understanding Pages & Sections",
          "Website Structure",
          "UI vs UX",
          "Planning a Website Before Building",
        ],
      },
      {
        title: "AI Website Workflow",
        sessions: [
          "Introduction to AI Website Building",
          "Choosing the Right AI Tools",
          "Generating Website Ideas",
          "Creating Website Structure With AI",
          "Writing Effective AI Prompts",
          "Turning Ideas Into Website Designs",
        ],
      },
      {
        title: "Website Design",
        sessions: [
          "Choosing a Design Direction",
          "Layout & Spacing",
          "Typography",
          "Color & Visual Hierarchy",
          "Creating High-Converting Sections",
          "Mobile Responsive Design",
        ],
      },
      {
        title: "Building With AI",
        sessions: [
          "Generating Website Components",
          "Creating Landing Pages",
          "Creating Business Websites",
          "Creating Portfolio Websites",
          "Adding Forms & CTAs",
          "Connecting Website Sections",
        ],
      },
      {
        title: "Website Content & Conversion",
        sessions: [
          "AI-Assisted Copywriting",
          "Writing Headlines",
          "Writing CTAs",
          "Creating Service/Product Sections",
          "Social Proof & Testimonials",
          "Conversion-Focused Website Structure",
        ],
      },
      {
        title: "Testing & Publishing",
        sessions: [
          "Checking Mobile Responsiveness",
          "Finding UI Problems",
          "Fixing Website Errors",
          "Basic SEO Setup",
          "Domain & Hosting Basics",
          "Publishing the Website",
        ],
      },
      {
        title: "Client Work & Monetization",
        sessions: [
          "Finding Website Clients",
          "Creating Website Packages",
          "Pricing Website Projects",
          "Creating Proposals",
          "Delivering Websites to Clients",
          "Getting Repeat Business",
        ],
      },
      {
        title: "Final Project",
        sessions: [
          "Build and publish a complete professional website using an AI-assisted workflow",
          "Present your live website + client proposal for review",
        ],
      },
    ],
  },
  "digital-marketing": {
    title: "Digital Marketing Course",
    tagline: "Attention, leads and sales — the complete growth system.",
    overview:
      "Learn the fundamentals of digital marketing: audience building, content marketing, social media, lead generation, paid advertising, analytics and conversion — applied to a real business.",
    forWho: [
      "Students and beginners starting a marketing career",
      "Business owners who want to market their own brand",
      "Freelancers offering marketing services to clients",
    ],
    youLearn: [
      "The digital marketing ecosystem, funnels and customer journey",
      "Brand positioning, personas and offer creation",
      "Content marketing and organic social media growth",
      "Lead generation, lead magnets and landing pages",
      "Paid ads fundamentals, metrics and optimization",
    ],
    outcomes: [
      "A complete marketing strategy for a real business",
      "A working lead-generation funnel you built yourself",
      "Fluency in the metrics that decide profitability",
    ],
    tools: ["Meta Ads Manager", "Google Analytics", "Instagram", "YouTube", "Canva", "ChatGPT", "WhatsApp Business"],
    careers: [
      "Digital Marketing Executive",
      "Social Media Manager",
      "Performance Marketer",
      "Lead Generation Specialist",
      "Freelance Marketing Consultant",
    ],
    duration: "10 weeks · self-paced",
    level: "All levels",
    totalSessions: "44+ Sessions",
    curriculum: [
      {
        title: "Digital Marketing Fundamentals",
        sessions: [
          "What Is Digital Marketing?",
          "Digital Marketing Ecosystem",
          "Understanding Your Target Audience",
          "Customer Journey",
          "Marketing Funnel",
          "Setting Marketing Goals",
        ],
      },
      {
        title: "Brand & Positioning",
        sessions: [
          "Finding Your Target Market",
          "Customer Persona",
          "Brand Positioning",
          "Unique Selling Proposition",
          "Offer Creation",
          "Building Trust Online",
        ],
      },
      {
        title: "Content Marketing",
        sessions: [
          "Content Marketing Fundamentals",
          "Content Pillars",
          "Content Ideas",
          "Short-Form Video Strategy",
          "Content Calendar",
          "Content That Generates Leads",
        ],
      },
      {
        title: "Social Media Marketing",
        sessions: [
          "Instagram Marketing",
          "Facebook Marketing",
          "YouTube Basics",
          "Profile Optimization",
          "Organic Growth Strategy",
          "Community Building",
        ],
      },
      {
        title: "Lead Generation",
        sessions: [
          "What Is Lead Generation?",
          "Lead Magnets",
          "Landing Pages",
          "Lead Capture",
          "WhatsApp/DM Lead Generation",
          "Lead Nurturing",
        ],
      },
      {
        title: "Paid Advertising",
        sessions: [
          "Introduction to Paid Ads",
          "Ad Campaign Structure",
          "Audience Targeting",
          "Ad Creative Basics",
          "Budget & Bidding Fundamentals",
          "Measuring Ad Performance",
        ],
      },
      {
        title: "Analytics & Optimization",
        sessions: [
          "Important Marketing Metrics",
          "Understanding Conversion Rate",
          "Cost Per Lead",
          "Customer Acquisition Cost",
          "Testing & Optimization",
          "Building a Simple Marketing Dashboard",
        ],
      },
      {
        title: "Final Project",
        sessions: [
          "Create a complete digital marketing strategy and lead-generation funnel for a sample business",
          "Present your funnel, metrics plan and content calendar",
        ],
      },
    ],
  },
  dropshipping: {
    title: "Dropshipping Course",
    tagline: "Start, run and scale a store without holding inventory.",
    overview:
      "The fundamentals of starting and operating a dropshipping business — product research, store creation, suppliers, marketing, order management, profitability and scaling.",
    forWho: [
      "Beginners who want to start an e-commerce business with low capital",
      "Sellers who want a structured product-research system",
      "Marketers who want to own a store instead of a service",
    ],
    youLearn: [
      "The dropshipping model, economics and customer journey",
      "Product research, validation and picking winning products",
      "Supplier research, fulfilment, shipping and returns",
      "Store structure, product pages and mobile optimization",
      "Organic + paid marketing, conversion and scaling",
    ],
    outcomes: [
      "A complete store plan with validated products",
      "A marketing funnel ready to drive first orders",
      "Clear grip on margins, metrics and operations",
    ],
    tools: ["Shopify", "AliExpress", "Meta Ads Manager", "Canva", "CapCut", "Google Sheets", "ChatGPT"],
    careers: [
      "Dropshipping Store Owner",
      "E-commerce Product Researcher",
      "E-commerce Marketer",
      "Store Setup Freelancer",
      "D2C Brand Operator",
    ],
    duration: "8 weeks · action-based",
    level: "Beginner → Pro",
    totalSessions: "44+ Sessions",
    curriculum: [
      {
        title: "Dropshipping Fundamentals",
        sessions: [
          "What Is Dropshipping?",
          "How the Dropshipping Model Works",
          "Advantages & Challenges",
          "Choosing a Business Model",
          "Understanding the Customer Journey",
          "Dropshipping Economics",
        ],
      },
      {
        title: "Product Research",
        sessions: [
          "Finding Product Opportunities",
          "Product Research Methods",
          "Understanding Product Demand",
          "Competitor Research",
          "Product Validation",
          "Selecting Winning Products",
        ],
      },
      {
        title: "Supplier & Fulfillment",
        sessions: [
          "Understanding Suppliers",
          "Supplier Research",
          "Evaluating Supplier Quality",
          "Shipping & Delivery Basics",
          "Order Fulfillment",
          "Customer Support & Returns",
        ],
      },
      {
        title: "Store Creation",
        sessions: [
          "Planning Your Store",
          "Store Structure",
          "Product Pages",
          "Product Descriptions",
          "Store Branding",
          "Mobile Optimization",
        ],
      },
      {
        title: "Marketing",
        sessions: [
          "Organic Marketing",
          "Short-Form Content",
          "Influencer Marketing",
          "Paid Advertising Fundamentals",
          "Creating Ad Creatives",
          "Building a Marketing Funnel",
        ],
      },
      {
        title: "Sales & Conversion",
        sessions: [
          "Pricing Products",
          "Creating Offers",
          "Upsells & Cross-Sells",
          "Conversion Optimization",
          "Cart Abandonment",
          "Customer Trust & Social Proof",
        ],
      },
      {
        title: "Operations & Scaling",
        sessions: [
          "Order Management",
          "Tracking Business Metrics",
          "Managing Customer Queries",
          "Improving Profit Margins",
          "Scaling Marketing",
          "Building a Sustainable Store",
        ],
      },
      {
        title: "Final Project",
        sessions: [
          "Create a complete dropshipping store plan including product selection, store structure and marketing strategy",
          "Present your store plan and unit economics",
        ],
      },
    ],
  },
  "ugc-content-creation": {
    title: "UGC Content Creation Course",
    tagline: "Get paid to create content brands actually use.",
    overview:
      "Create authentic user-generated content for brands, build a UGC portfolio, pitch brands, produce high-quality videos and monetize your UGC skills with clear pricing and deliverables.",
    forWho: [
      "Creators who want brand deals without a big following",
      "Students looking for a phone-first income skill",
      "Freelancers adding UGC to their service menu",
    ],
    youLearn: [
      "What UGC is, how brands use it and where the money is",
      "Niche selection, content angles and creator positioning",
      "Hooks, scripts, demos, testimonials and storytelling",
      "Shooting and editing UGC on a smartphone",
      "Portfolio building, cold outreach, pricing and negotiation",
    ],
    outcomes: [
      "A polished UGC portfolio with sample videos",
      "A brand outreach system with pitch templates",
      "Confident pricing, deliverables and usage-rights knowledge",
    ],
    tools: ["Smartphone Camera", "CapCut", "Canva", "Instagram", "Notion", "Gmail", "ChatGPT"],
    careers: [
      "UGC Creator",
      "Brand Content Creator",
      "Product Video Specialist",
      "Social Media Content Freelancer",
      "Creator-Led Content Studio Owner",
    ],
    duration: "8 weeks · project-based",
    level: "All levels",
    totalSessions: "44+ Sessions",
    curriculum: [
      {
        title: "UGC Fundamentals",
        sessions: [
          "What Is UGC?",
          "Types of UGC Content",
          "UGC vs Influencer Content",
          "How Brands Use UGC",
          "UGC Creator Opportunities",
          "UGC Industry Basics",
        ],
      },
      {
        title: "Finding Your UGC Niche",
        sessions: [
          "Choosing a Niche",
          "Understanding Your Target Brands",
          "Finding Content Angles",
          "Building Your Creator Identity",
          "Creating a Content Style",
          "Developing Your UGC Positioning",
        ],
      },
      {
        title: "Content Creation",
        sessions: [
          "UGC Video Structure",
          "Writing Hooks",
          "Creating Scripts",
          "Product Demonstrations",
          "Testimonials & Reviews",
          "Storytelling for UGC",
        ],
      },
      {
        title: "Shooting & Editing",
        sessions: [
          "Smartphone Camera Basics",
          "Lighting",
          "Framing & Composition",
          "Recording Voiceovers",
          "B-Roll",
          "Editing UGC Videos",
        ],
      },
      {
        title: "UGC Portfolio",
        sessions: [
          "Creating Sample UGC",
          "Choosing Portfolio Pieces",
          "Creating a UGC Portfolio",
          "Writing a Creator Bio",
          "Creating a Portfolio Presentation",
          "Making Your Portfolio Brand-Friendly",
        ],
      },
      {
        title: "Getting Brand Deals",
        sessions: [
          "Finding Potential Brands",
          "Brand Research",
          "Cold Outreach",
          "Writing Pitch Messages",
          "Email & DM Outreach",
          "Following Up With Brands",
        ],
      },
      {
        title: "Pricing & Monetization",
        sessions: [
          "How to Price UGC",
          "Understanding UGC Deliverables",
          "Packages & Pricing",
          "Usage Rights Basics",
          "Negotiation Fundamentals",
          "Building Long-Term Brand Relationships",
        ],
      },
      {
        title: "Final Project",
        sessions: [
          "Create a complete UGC portfolio with sample videos and a brand outreach system",
          "Send your first 10 brand pitches and track responses",
        ],
      },
    ],
  },
};

export const WHY = [
  { icon: Hammer, title: "Practical Learning", desc: "Project-driven curriculum — apply every concept the same week." },
  { icon: GraduationCap, title: "Real-World Skills", desc: "Frameworks used by working operators, not theory." },
  { icon: HeartHandshake, title: "Personal Mentorship", desc: "Get unstuck with mentors who've actually shipped." },
  { icon: Users, title: "Community Support", desc: "Surrounded by peers building in public, every day." },
  { icon: Compass, title: "Career Growth Focus", desc: "Roadmaps that compound from learning to earning." },
  { icon: Rocket, title: "Future-Ready Learning", desc: "Stay ahead with AI, creator economy and modern tooling." },
];

export const JOURNEY = [
  { step: "01", title: "Choose Your Package", desc: "Pick the access level that matches your goals." },
  { step: "02", title: "Select Your Courses", desc: "Choose the courses you want inside your package." },
  { step: "03", title: "Get Instant Access", desc: "Join the community and start learning immediately." },
  { step: "04", title: "Learn, Build & Practice", desc: "Apply concepts to live projects with mentor support." },
  { step: "05", title: "Start Earning", desc: "Land clients, gigs and turn skills into income." },
];

// ---------- PACKAGES ----------
export const PACKAGE_FEATURES = [
  { key: "courses", label: "Course Access" },
  { key: "community", label: "WhatsApp Community Access" },
  { key: "support", label: "Student Support" },
  { key: "resources", label: "Learning Resources" },
  { key: "updates", label: "Course Updates" },
  { key: "materials", label: "Practical Learning Materials" },
  { key: "mentorship", label: "Personal Mentorship" },
  { key: "guidance", label: "Guidance Sessions" },
  { key: "assignments", label: "Practical Assignments" },
  { key: "skillDev", label: "Skill Development Support" },
  { key: "live", label: "Live Guidance Sessions" },
  { key: "career", label: "Career Growth Support" },
  { key: "freelance", label: "Freelancing Growth Guidance" },
  { key: "roadmap", label: "Skill Development Roadmaps" },
  { key: "accountability", label: "Accountability Support" },
  { key: "exclusiveUpdates", label: "Exclusive Updates" },
  { key: "networking", label: "Networking Opportunities" },
  { key: "club", label: "1% Community Club Access" },
  { key: "premiumExp", label: "Premium Student Experience" },
  { key: "futureCourses", label: "Future Course Updates" },
  { key: "liveQA", label: "Monthly Live Q&A Sessions" },
];

export const PACKAGES = [
  {
    id: "elite",
    name: "E-Lite Package",
    tagline: "Start Your Skill Journey",
    description:
      "Designed for students and beginners who want to focus on two core skills and learn at their own pace with community support.",
    eyebrow: "Starter",
    get badge() { return pctOff(PRICING.tiers.elite); },
    get originalPrice() { return PRICING.tiers.elite.strike; },
    get offerPrice() { return PRICING.tiers.elite.price; },
    courseAccess: "Choose Any 2 Courses",
    highlight: false,
    cta: "Choose E-Lite",
    features: {
      courses: "Any 2 Courses",
      community: true,
      support: true,
      resources: true,
      updates: true,
      materials: true,
      mentorship: false,
      guidance: false,
      assignments: false,
      skillDev: false,
      live: false,
      career: false,
      freelance: false,
      roadmap: false,
      accountability: false,
      exclusiveUpdates: false,
      networking: false,
      club: false,
      premiumExp: false,
      futureCourses: false,
      liveQA: false,
    },
    includes: [
      "Access to Any 2 Courses",
      "WhatsApp Community Access",
      "Student Support",
      "Learning Resources",
      "Course Updates",
      "Practical Learning Materials",
    ],
    excludes: ["Personal Mentorship", "1% Community Club Access", "Priority Support"],
  },
  {
    id: "basic",
    name: "Basic Package",
    tagline: "Build Multiple Skills",
    description:
      "Perfect for learners who want to combine multiple high-income skills and accelerate their growth with mentorship and support.",
    eyebrow: "Most Chosen",
    get badge() { return pctOff(PRICING.tiers.basic); },
    get originalPrice() { return PRICING.tiers.basic.strike; },
    get offerPrice() { return PRICING.tiers.basic.price; },
    courseAccess: "Choose 3 Courses + 2 E-Lite Courses Free (5 Total)",
    highlight: false,
    cta: "Choose Basic",
    features: {
      courses: "3 Courses + 2 Free (5 Total)",
      community: true,
      support: true,
      resources: true,
      updates: true,
      materials: true,
      mentorship: true,
      guidance: true,
      assignments: true,
      skillDev: true,
      live: false,
      career: false,
      freelance: false,
      roadmap: false,
      accountability: false,
      exclusiveUpdates: false,
      networking: false,
      club: false,
      premiumExp: false,
      futureCourses: false,
      liveQA: false,
    },
    includes: [
      "Choose 3 Courses + 2 E-Lite Courses Free (5 Total)",
      "WhatsApp Community Access",
      "Student Support",
      "Personal Mentorship",
      "Guidance Sessions",
      "Learning Resources",
      "Course Updates",
      "Practical Assignments",
      "Skill Development Support",
    ],
    excludes: ["1% Community Club Access"],
  },
  {
    id: "premium",
    name: "Premium Package",
    tagline: "Complete Transformation Package",
    description:
      "Full access to the SkillPreneur Academy ecosystem — all courses, personal mentorship, exclusive community access, and continuous growth support.",
    eyebrow: "Most Popular · Recommended · Best Value",
    badge: "Limited Time Offer",
    get originalPrice() { return PRICING.tiers.premium.strike; },
    get offerPrice() { return PRICING.tiers.premium.price; },
    get courseAccess() { return `All ${PRICING.totalCourses} Courses Included`; },
    highlight: true,
    cta: "Choose Premium",
    features: {
      get courses() { return `All ${PRICING.totalCourses} Courses`; },
      community: true,
      support: "Priority",
      resources: "Premium",
      updates: true,
      materials: true,
      mentorship: "Free",
      guidance: true,
      assignments: true,
      skillDev: true,
      live: true,
      career: true,
      freelance: true,
      roadmap: true,
      accountability: true,
      exclusiveUpdates: true,
      networking: true,
      club: true,
      premiumExp: true,
      futureCourses: true,
      liveQA: true,
    },
    includes: [
      `Access to All ${PRICING.totalCourses} Courses`,
      "Personal Mentorship (Free)",
      "WhatsApp Community Access",
      "Priority Student Support",
      "Premium Learning Resources",
      "Live Guidance Sessions",
      "Career Growth Support",
      "Freelancing Growth Guidance",
      "Skill Development Roadmaps",
      "Accountability Support",
      "Exclusive Updates",
      "Networking Opportunities",
      "1% Community Club Access",
      "Premium Student Experience",
      "Future Course Updates",
      "Monthly Live Q&A Sessions",
    ],
    excludes: [],
  },
];

export const TESTIMONIALS = [
  {
    name: "Student Name",
    role: "Premium Package Learner",
    quote:
      "Picking the Premium package gave me clarity and the mentorship I needed to actually start building. Placeholder testimonial — real story coming soon.",
  },
  {
    name: "Student Name",
    role: "Basic Package Learner",
    quote:
      "Three courses, weekly mentorship, and a community that holds you accountable. Placeholder testimonial — real story coming soon.",
  },
  {
    name: "Student Name",
    role: "E-Lite Package Learner",
    quote:
      "I started simple with one course and the community kept me consistent. Placeholder testimonial — real story coming soon.",
  },
];

export const FAQS = [
  {
    q: "How are SkillPreneur Academy packages different from buying a single course?",
    a: "Packages are designed as a complete learning experience — community, mentorship, resources and support — not just video content. You pick the courses you want from inside the package.",
  },
  {
    q: "Which package should I choose?",
    a: "Choose E-Lite if you're starting out with 2 skills, Basic if you want 3 courses plus 2 E-Lite courses free (5 in total) with mentorship, and Premium if you want full access to all 10 courses, mentorship, the 1% Community Club and the most growth.",
  },
  {
    q: "Can I switch courses inside my package later?",
    a: "Course selection happens during onboarding. If you want to change, our student support team will guide you based on your package terms.",
  },
  {
    q: "What does Personal Mentorship include?",
    a: "Structured 1:1 guidance from mentors, weekly direction, project feedback and a clear roadmap for your goals — included in Basic and Premium.",
  },
  {
    q: "What is the 1% Community Club?",
    a: "An exclusive private community inside Premium for the most committed learners — deeper networking, accountability, exclusive sessions and direct access to operators.",
  },
  {
    q: "How do I get support after enrolling?",
    a: "Every package includes student support. Premium learners get priority support and faster response times across WhatsApp and email.",
  },
  {
    q: "How is the learning delivered?",
    a: "A blend of structured lessons, weekly assignments, peer reviews, live sessions and mentor guidance — all accessible from any device.",
  },
];
